import { useCallback, useEffect } from 'react'
import { useNavigation } from '../contexts/NavigationContext'

/**
 * Hook for managing navigation state with localStorage persistence
 * Provides convenient access to navigation state and utilities
 */
export const useNavigationState = () => {
  const navigation = useNavigation()

  // Remember last expanded section if preference is enabled
  const toggleSectionWithMemory = useCallback((sectionId: string) => {
    if (navigation.userPreferences.rememberLastSection) {
      // Store the last expanded section
      navigation.setViewState('lastExpandedSection', sectionId)
    }
    navigation.toggleSection(sectionId)
  }, [navigation])

  // Get section state with fallback
  const getSectionState = useCallback((sectionId: string, defaultValue: boolean = false) => {
    return navigation.expandedSections[sectionId] ?? defaultValue
  }, [navigation.expandedSections])

  // Set multiple section states at once
  const setMultipleSections = useCallback((sections: Record<string, boolean>) => {
    navigation.setExpandedSections({
      ...navigation.expandedSections,
      ...sections
    })
  }, [navigation])

  // Collapse all sections
  const collapseAllSections = useCallback(() => {
    const collapsedSections = Object.keys(navigation.expandedSections).reduce((acc, key) => {
      acc[key] = false
      return acc
    }, {} as Record<string, boolean>)
    navigation.setExpandedSections(collapsedSections)
  }, [navigation])

  // Expand all sections
  const expandAllSections = useCallback(() => {
    const expandedSections = Object.keys(navigation.expandedSections).reduce((acc, key) => {
      acc[key] = true
      return acc
    }, {} as Record<string, boolean>)
    navigation.setExpandedSections(expandedSections)
  }, [navigation])

  // Get navigation state summary
  const getNavigationSummary = useCallback(() => {
    return {
      sidebarOpen: navigation.isSidebarOpen,
      expandedSectionsCount: Object.values(navigation.expandedSections).filter(Boolean).length,
      totalSections: Object.keys(navigation.expandedSections).length,
      topicsSidebarCollapsed: navigation.isTopicsSidebarCollapsed,
      userPreferences: navigation.userPreferences
    }
  }, [navigation])

  // Check if navigation state has changed from defaults
  const hasCustomState = useCallback(() => {
    const defaultSidebarOpen = navigation.userPreferences.sidebarDefaultOpen
    return (
      navigation.isSidebarOpen !== defaultSidebarOpen ||
      Object.values(navigation.expandedSections).some(Boolean) ||
      navigation.isTopicsSidebarCollapsed !== false ||
      Object.keys(navigation.viewStates).length > 0
    )
  }, [navigation])

  return {
    // Core navigation state
    isSidebarOpen: navigation.isSidebarOpen,
    setIsSidebarOpen: navigation.setIsSidebarOpen,
    toggleSidebar: navigation.toggleSidebar,
    
    // Section management
    expandedSections: navigation.expandedSections,
    setExpandedSections: navigation.setExpandedSections,
    toggleSection: toggleSectionWithMemory,
    getSectionState,
    setMultipleSections,
    collapseAllSections,
    expandAllSections,
    
    // Topics sidebar
    isTopicsSidebarCollapsed: navigation.isTopicsSidebarCollapsed,
    setIsTopicsSidebarCollapsed: navigation.setIsTopicsSidebarCollapsed,
    toggleTopicsSidebar: navigation.toggleTopicsSidebar,
    
    // User preferences
    userPreferences: navigation.userPreferences,
    updateUserPreference: navigation.updateUserPreference,
    
    // View states
    viewStates: navigation.viewStates,
    setViewState: navigation.setViewState,
    getViewState: navigation.getViewState,
    
    // Utilities
    resetAllNavigation: navigation.resetAllNavigation,
    exportNavigationState: navigation.exportNavigationState,
    importNavigationState: navigation.importNavigationState,
    getNavigationSummary,
    hasCustomState
  }
}
